import { useEffect, useState, useContext, useRef, useCallback } from 'react';
import { Box, Button } from '@ltvco/refresh-lib/theme';
import { useForm } from 'react-hook-form';
import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useTokenExIframe } from './hooks/useTokenExIframe';
import { usePaymentFlow } from './hooks/usePaymentFlow';
import { TokenExFormFields } from './components/TokenExFormFields';
import TermsAndConditions from './components/TermsAndConditions';
import {
  DEFAULT_TOKENEX_EVENT_ORIGIN,
  messageHandler,
} from '@ltvco/refresh-lib/payments';

interface TokenExFormData {
  email: string;
  first_name: string;
  last_name: string;
  postal_code: string;
  expiration_month: string;
  expiration_year: string;
  expiration_date: string;
  verification_number: string;
  card_number: string;
  tokenex_token: string;
  tokenex_input: string;
  [key: string]: string;
}

interface TokenExSubscribeProps {
  createCardQuery: any;
  terms1: boolean;
  terms2: boolean;
  setTerms1?: (checked: boolean) => void;
  setTerms2?: (checked: boolean) => void;
  onProcessingStart?: () => void;
  onSuccess?: () => void;
  onError?: (errors?: string[]) => void;
  initialEmail?: string;
  initialFirstName?: string;
  initialLastName?: string;
}

export const TokenExSubscribe = ({
  createCardQuery,
  terms1,
  terms2,
  setTerms1,
  setTerms2,
  onProcessingStart,
  onSuccess,
  onError,
  initialEmail,
  initialFirstName,
  initialLastName,
}: TokenExSubscribeProps) => {
  // Hooks
  const [isAddingCard, setIsAddingCard] = useState<boolean>(false);
  const [ctaClicked, setCtaClicked] = useState<boolean>(false);
  const { logError } = useContext(AppConfig);
  const { processPayment, processingPayment } = usePaymentFlow();
  const {
    isLoading,
    isError,
    iframeExpired,
    iframeInputInvalid,
    isIframeEmpty,
    tokenExLabelRef,
    tokenExWrapperRef,
    handleTokenization,
    createEventHandlers,
  } = useTokenExIframe();
  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
    trigger,
    getValues,
    setValue,
    watch,
  } = useForm<TokenExFormData>({
    mode: 'onChange',
    defaultValues: {
      email: initialEmail || '',
      first_name: initialFirstName || '',
      last_name: initialLastName || '',
    },
  });
  const ctaClickedRef = useRef<boolean>(false);

  // Callbacks and Handlers
  const handleFormSubmit = async () => {
    setCtaClicked(true);
    setIsAddingCard(true);
    const result = await trigger();
    if (!result) {
      setIsAddingCard(false);
      return;
    }
    onProcessingStart?.();
    handleTokenization();
  };

  const handleTerms1Change = useCallback(
    (checked: boolean) => {
      setTerms1?.(checked);
    },
    [setTerms1]
  );

  const handleTerms2Change = useCallback(
    (checked: boolean) => {
      setTerms2?.(checked);
    },
    [setTerms2]
  );

  const eventHandlers = createEventHandlers(
    (_formData, token, maskedNumber) => {
      setIsAddingCard(true);
      const currentFormData = getValues();
      processPayment(currentFormData, token, maskedNumber, createCardQuery, {
        onProcessingStart,
        onSuccess: () => {
          onSuccess?.();
        },
        onError: (errors) => {
          onError?.(errors);
        },
      }).finally(() => {
        setIsAddingCard(false);
      });
    },
    { ctaClickedRef }
  );

  const reference = (event: MessageEvent) => {
    if (event.origin !== DEFAULT_TOKENEX_EVENT_ORIGIN) {
      return;
    }
    messageHandler(event, eventHandlers, logError);
  };

  // Effects
  useEffect(() => {
    register('expiration_month');
    register('expiration_year');
  }, [register]);

  useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (name !== 'expiration_date') return;
      const exp = values.expiration_date?.trim() || '';
      const match = exp.match(/^(0?[1-9]|1[0-2])\/(\d{2}|\d{4})$/);
      if (!match) {
        setValue('expiration_month', '', {
          shouldDirty: false,
          shouldValidate: false,
        });
        setValue('expiration_year', '', {
          shouldDirty: false,
          shouldValidate: false,
        });
        return;
      }
      const monthRaw = match[1];
      const yearRaw = match[2];
      const month = monthRaw.padStart(2, '0');
      const year = yearRaw;
      setValue('expiration_month', month, {
        shouldDirty: false,
        shouldValidate: true,
      });
      setValue('expiration_year', year, {
        shouldDirty: false,
        shouldValidate: true,
      });
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  useEffect(() => {
    const current = getValues();
    if (typeof initialEmail === 'string' && current.email !== initialEmail) {
      setValue('email', initialEmail, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
    if (
      typeof initialFirstName === 'string' &&
      current.first_name !== initialFirstName
    ) {
      setValue('first_name', initialFirstName, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
    if (
      typeof initialLastName === 'string' &&
      current.last_name !== initialLastName
    ) {
      setValue('last_name', initialLastName, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [initialEmail, initialFirstName, initialLastName, getValues, setValue]);

  useEffect(() => {
    ctaClickedRef.current = ctaClicked;
  }, [ctaClicked]);

  useEffect(() => {
    window.addEventListener('message', reference);
    return () => {
      window.removeEventListener('message', reference);
    };
  }, [eventHandlers]);

  return (
    <Box sx={{ mt: 2 }}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <TokenExFormFields
          register={register}
          errors={errors}
          tokenExLabelRef={tokenExLabelRef}
          tokenExWrapperRef={tokenExWrapperRef}
          iframeInputInvalid={iframeInputInvalid}
        />

        <TermsAndConditions
          terms1={terms1}
          terms2={terms2}
          onChangeTerms1={handleTerms1Change}
          onChangeTerms2={handleTerms2Change}
        />

        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          type="button"
          disabled={
            !isValid ||
            isAddingCard ||
            iframeInputInvalid ||
            iframeExpired ||
            isIframeEmpty ||
            !terms1 ||
            !terms2 ||
            processingPayment ||
            isLoading ||
            isError
          }
          onClick={handleFormSubmit}
        >
          {isAddingCard || createCardQuery.isLoading || processingPayment
            ? 'Processing...'
            : 'Complete Secure Checkout'}
        </Button>
      </form>
    </Box>
  );
};
